import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Award, Droplets, TreePine, Wrench, CheckCircle2, ArrowRight, Leaf, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    projectType: '',
    message: '',
    contactMethod: 'phone'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const projectImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_f39af61b-be2b-4448-bb55-459d3723e5c8/artifacts/5aue49r1_image.png',
      title: 'Custom Water Feature & Hardscape',
      description: 'Natural stone water feature with flowing design'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_f39af61b-be2b-4448-bb55-459d3723e5c8/artifacts/jqkfhh0c_image.png',
      title: 'Precision Garden Design',
      description: 'Vibrant seasonal plantings with meticulous lawn care'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_f39af61b-be2b-4448-bb55-459d3723e5c8/artifacts/030r7hvj_image.png',
      title: 'Gravel Pathway & Stone Elements',
      description: 'Contemporary hardscape with river rock accents'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_f39af61b-be2b-4448-bb55-459d3723e5c8/artifacts/yyal72kt_image.png',
      title: 'Detailed Planting & Design',
      description: 'Thoughtful plant selection and artistic arrangement'
    }
  ];

  const services = [
    {
      icon: TreePine,
      title: 'Full Backyard Overhauls',
      description: 'Transform overgrown or neglected lots into functional, beautiful outdoor living areas that elevate your property value and lifestyle.'
    },
    {
      icon: Droplets,
      title: 'Hardscape & Water Features',
      description: 'Specialized installation of custom fountains, gravel pathways, stone accents, and water features that become the centerpiece of your landscape.'
    },
    {
      icon: Leaf,
      title: 'Precision Maintenance',
      description: 'High-end pruning, lawn care, and ornamental plant health services to keep your outdoor space looking immaculate year-round.'
    },
    {
      icon: Wrench,
      title: 'Heavy-Duty Cleanups',
      description: 'Expert removal of stubborn, overgrown vegetation and site grading to reclaim and restore your outdoor space.'
    }
  ];

  const usps = [
    {
      title: 'All-Weather Reliability',
      description: "Famous for working through Portland's toughest rain and hail to meet deadlines. Your project timeline matters to us."
    },
    {
      title: 'Clean-Site Guarantee',
      description: 'We leave your property cleaner than we found it, every single day. No exceptions.'
    },
    {
      title: 'Timeline Integrity',
      description: 'If we say it will take five days, it takes five days. Punctuality and transparency are our foundation.'
    },
    {
      title: 'The Bonus Touch',
      description: 'Known for adding thoughtful extras like complimentary flower plantings to finish projects perfectly.'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Web3Forms will be integrated here
    // For now, just show a message
    setTimeout(() => {
      setSubmitMessage('Thank you! Elon will contact you within 24 hours.');
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        projectType: '',
        message: '',
        contactMethod: 'phone'
      });
    }, 1000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-800 rounded-lg flex items-center justify-center">
                <TreePine className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-stone-900">Felton Landscape</h1>
                <p className="text-xs text-stone-600">Portland's Premier Service</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#services" className="text-stone-700 hover:text-emerald-800 transition-colors font-medium">Services</a>
              <a href="#gallery" className="text-stone-700 hover:text-emerald-800 transition-colors font-medium">Gallery</a>
              <a href="#why-felton" className="text-stone-700 hover:text-emerald-800 transition-colors font-medium">Why Us</a>
              <Button onClick={scrollToContact} className="bg-emerald-800 hover:bg-emerald-900 transition-all duration-300">
                Get Quote
              </Button>
            </div>
            <a href="tel:5035398093" className="md:hidden">
              <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900">
                <Phone className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-stone-50 to-amber-50 opacity-70"></div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full mb-6 font-medium text-sm">
              <Star className="w-4 h-4 fill-emerald-800" />
              5.0 Google Rating • Owner-Operated
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-stone-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Transformative Outdoor Living Spaces
            </h2>
            <p className="text-lg lg:text-xl text-stone-700 mb-8 leading-relaxed max-w-2xl mx-auto">
              Boutique landscaping in Portland with punctuality, transparency, and meticulous craftsmanship. 
              Work directly with Elon, your dedicated landscape expert.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button onClick={scrollToContact} size="lg" className="bg-emerald-800 hover:bg-emerald-900 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg">
                Request Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="tel:5035398093">
                <Button size="lg" variant="outline" className="border-2 border-emerald-800 text-emerald-800 hover:bg-emerald-50 px-8 py-6 text-lg transition-all duration-300">
                  <Phone className="mr-2 w-5 h-5" />
                  (503) 539-8093
                </Button>
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-800 mb-2">15+</div>
                <div className="text-sm text-stone-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-800 mb-2">100%</div>
                <div className="text-sm text-stone-600">On-Time Delivery</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-800 mb-2">5.0★</div>
                <div className="text-sm text-stone-600">Client Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-stone-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  The Felton Difference: Personal Touch, Professional Results
                </h3>
                <p className="text-lg text-stone-700 mb-6 leading-relaxed">
                  Felton Landscape Maintenance isn't just another landscaping service. We're an owner-operated boutique firm where you work directly with Elon, not a faceless corporation.
                </p>
                <p className="text-lg text-stone-700 mb-8 leading-relaxed">
                  Every project receives personal attention, from initial consultation to final walk-through. This hands-on approach ensures your vision becomes reality with the quality and care it deserves.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-800 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Direct Owner Communication</h4>
                      <p className="text-stone-600">Elon is on-site and reachable throughout your project</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-800 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Local Portland Expertise</h4>
                      <p className="text-stone-600">Deep understanding of Pacific Northwest landscaping</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-800 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Meticulous Craftsmanship</h4>
                      <p className="text-stone-600">Every detail matters in creating your perfect outdoor space</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={projectImages[0].url} 
                    alt="Felton Landscape Work" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-emerald-800 text-white p-6 rounded-xl shadow-xl">
                  <div className="text-2xl font-bold mb-1">Serving Portland</div>
                  <div className="text-emerald-100">NW Portland • Bethany • Beyond</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-stone-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-5xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Our Services
              </h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                Comprehensive landscaping solutions tailored to transform and maintain your outdoor spaces
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden">
                    <CardContent className="p-8">
                      <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-800 transition-colors duration-300">
                        <Icon className="w-7 h-7 text-emerald-800 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h4 className="text-2xl font-bold text-stone-900 mb-4">{service.title}</h4>
                      <p className="text-stone-600 leading-relaxed">{service.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-5xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Our Work
              </h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                See the transformative results we've achieved for Portland homeowners
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {projectImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
                  <div className="aspect-[4/3] overflow-hidden bg-stone-200">
                    <img 
                      src={image.url} 
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <h4 className="text-xl font-bold mb-2">{image.title}</h4>
                      <p className="text-stone-200">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Felton Section */}
      <section id="why-felton" className="py-20 bg-emerald-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Why Choose Felton?
              </h3>
              <p className="text-lg text-emerald-100 max-w-2xl mx-auto">
                Our commitment to excellence sets us apart from traditional landscaping companies
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {usps.map((usp, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-8 hover:bg-white/20 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <Award className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-2xl font-bold mb-3">{usp.title}</h4>
                      <p className="text-emerald-100 leading-relaxed">{usp.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-stone-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl lg:text-5xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Request Your Free Quote
              </h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                Ready to transform your outdoor space? Fill out the form below and Elon will get back to you personally within 24 hours.
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 space-y-6">
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-stone-900 mb-4 text-lg">Contact Information</h4>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-emerald-800 mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-stone-600 mb-1">Phone</div>
                          <a href="tel:5035398093" className="font-semibold text-stone-900 hover:text-emerald-800 transition-colors">
                            (503) 539-8093
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-emerald-800 mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-stone-600 mb-1">Address</div>
                          <div className="font-semibold text-stone-900">
                            17939 NW Andria Ave<br />
                            Portland, OR 97229
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-emerald-800 mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-stone-600 mb-1">Response Time</div>
                          <div className="font-semibold text-stone-900">Within 24 Hours</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-lg bg-emerald-800 text-white">
                  <CardContent className="p-6">
                    <h4 className="font-bold mb-2 text-lg">Service Areas</h4>
                    <p className="text-emerald-100 text-sm">
                      Portland, NW Portland, Bethany, and surrounding areas in the greater Portland metro
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-xl bg-white">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-stone-900 mb-2">Your Name *</label>
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-stone-900 mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                            placeholder="(503) 555-0123"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-900 mb-2">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-900 mb-2">Service Address *</label>
                        <input
                          type="text"
                          name="address"
                          required
                          value={formData.address}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                          placeholder="Street address, City, ZIP"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-900 mb-2">Type of Project *</label>
                        <select
                          name="projectType"
                          required
                          value={formData.projectType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200 bg-white"
                        >
                          <option value="">Select a service...</option>
                          <option value="monthly-maintenance">Monthly Maintenance</option>
                          <option value="backyard-overhaul">Full Backyard Overhaul</option>
                          <option value="hardscape-water">Hardscape/Water Feature</option>
                          <option value="one-time-cleanup">One-Time Cleanup</option>
                          <option value="other">Other / Not Sure</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-900 mb-2">Project Description *</label>
                        <textarea
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleInputChange}
                          rows="4"
                          className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200 resize-none"
                          placeholder="Please describe your vision and any specific requirements..."
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-900 mb-3">Best Way to Reach You</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="contactMethod"
                              value="phone"
                              checked={formData.contactMethod === 'phone'}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-emerald-800 focus:ring-emerald-800"
                            />
                            <span className="text-stone-700">Phone</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="contactMethod"
                              value="email"
                              checked={formData.contactMethod === 'email'}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-emerald-800 focus:ring-emerald-800"
                            />
                            <span className="text-stone-700">Email</span>
                          </label>
                        </div>
                      </div>
                      {submitMessage && (
                        <div className="bg-emerald-100 border border-emerald-300 text-emerald-800 px-4 py-3 rounded-lg">
                          {submitMessage}
                        </div>
                      )}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        {isSubmitting ? 'Sending...' : 'Submit Request'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <p className="text-sm text-stone-600 text-center">
                        By submitting this form, you agree to be contacted about your landscaping project.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-800 rounded-lg flex items-center justify-center">
                    <TreePine className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Felton Landscape</h3>
                    <p className="text-xs text-stone-400">Maintenance</p>
                  </div>
                </div>
                <p className="text-stone-400 text-sm leading-relaxed">
                  Owner-operated boutique landscaping serving Portland with meticulous craftsmanship and personal attention.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Contact</h4>
                <div className="space-y-3 text-sm text-stone-400">
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                    <a href="tel:5035398093" className="hover:text-white transition-colors">(503) 539-8093</a>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                    <div>17939 NW Andria Ave<br />Portland, OR 97229</div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-4">Service Areas</h4>
                <ul className="space-y-2 text-sm text-stone-400">
                  <li>Portland, OR</li>
                  <li>NW Portland</li>
                  <li>Bethany</li>
                  <li>Surrounding Areas</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-stone-800 pt-8 text-center text-sm text-stone-500">
              <p>© 2025 Felton Landscape Maintenance. All rights reserved. Licensed & Insured.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;